import { Controller } from "@hotwired/stimulus"

// Neutraler §40-SGB-XI-Zuschuss-Rechner der Treppenlift-/Badumbau-Pillar-Seiten.
// Alle Daten kommen server-seitig als JSON (ProductPillarsController#zuschuss_data),
// gerechnet wird nur hier. Ergebnis erscheint ohne Lead-Formular — das ist der
// bewusste Unterschied zu den Lead-gated Preis-Schätzern (Cms::*CalculatorComponent).
export default class extends Controller {
  static values = { data: Object }

  static targets = [
    "pflegegradButton", "personenButton", "massnahmeSelect",
    "zuschuss", "zuschussHint", "noPgNote", "verdict",
    "kostenRange", "eigenanteilRange", "eigenanteilHint",
    "plzInput", "plzResult", "landesHint",
    "resultSummary", "stickyBar", "stickyZuschuss"
  ]

  connect() {
    this.formatter = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 })
    this.selectedPflegegrad = 2
    this.selectedPersonen = 1
    this.highlight(this.pflegegradButtonTargets, 2)
    this.highlight(this.personenButtonTargets, 1)
    this.calculate()
    this.element.dataset.ready = "true"
    this.observeResultCard()
  }

  disconnect() {
    this.observer?.disconnect()
    if (this.onScroll) window.removeEventListener("scroll", this.onScroll)
  }

  // Phone sticky bar (lg:hidden): visible while the result summary is off screen,
  // hidden again at the page end so it never covers the footer links
  observeResultCard() {
    if (!this.hasStickyBarTarget || !this.hasResultSummaryTarget || !("IntersectionObserver" in window)) return
    this.summaryVisible = true
    this.observer = new IntersectionObserver(([entry]) => {
      this.summaryVisible = entry.isIntersecting
      this.updateStickyBar()
    })
    this.observer.observe(this.resultSummaryTarget)
    this.onScroll = () => this.updateStickyBar()
    window.addEventListener("scroll", this.onScroll, { passive: true })
  }

  updateStickyBar() {
    const atEnd = window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 160
    const show = !this.summaryVisible && !atEnd
    this.stickyBarTarget.classList.toggle("translate-y-full", !show)
    this.stickyBarTarget.inert = !show
    this.stickyBarTarget.dataset.visible = show ? "1" : "0"
  }

  selectPflegegrad(event) {
    this.selectedPflegegrad = parseInt(event.currentTarget.dataset.value)
    this.highlight(this.pflegegradButtonTargets, this.selectedPflegegrad)
    this.calculate()
  }

  selectPersonen(event) {
    this.selectedPersonen = parseInt(event.currentTarget.dataset.value)
    this.highlight(this.personenButtonTargets, this.selectedPersonen)
    this.calculate()
  }

  // Segmented buttons: filled when selected, grey tone otherwise
  highlight(buttons, value) {
    buttons.forEach(btn => {
      const isSelected = parseInt(btn.dataset.value) === value
      btn.setAttribute("aria-pressed", String(isSelected))
      btn.classList.toggle("bg-ph24_new_blue", isSelected)
      btn.classList.toggle("text-white", isSelected)
      btn.classList.toggle("bg-ph24_new_gray_dark", !isSelected)
      btn.classList.toggle("text-ph24_new_blue", !isSelected)
      btn.classList.toggle("hover:bg-ph24_new_blue/15", !isSelected)
    })
  }

  calculate() {
    const data = this.dataValue
    const hasPflegegrad = this.selectedPflegegrad > 0
    const personen = Math.min(this.selectedPersonen, data.max_personen)
    const zuschuss = hasPflegegrad ? data.zuschuss_pro_person * personen : 0
    const fmt = (n) => this.formatter.format(n)

    this.zuschussTarget.textContent = fmt(zuschuss)
    if (this.hasStickyZuschussTarget) this.stickyZuschussTarget.textContent = fmt(zuschuss)
    if (this.hasNoPgNoteTarget) this.noPgNoteTarget.classList.toggle("hidden", hasPflegegrad)

    if (this.hasZuschussHintTarget) {
      let hint = "Ohne Pflegegrad kein Zuschuss"
      if (hasPflegegrad && personen === 1) {
        hint = "§ 40 SGB XI · 1 Person mit Pflegegrad"
      } else if (hasPflegegrad && personen === data.max_personen) {
        hint = `§ 40 SGB XI · ${personen} Personen (Höchstbetrag)`
      } else if (hasPflegegrad) {
        hint = `§ 40 SGB XI · ${personen} Personen mit Pflegegrad`
      }
      this.zuschussHintTarget.textContent = hint
    }

    const massnahme = data.massnahmen[this.massnahmeSelectTarget.value]
    if (!massnahme) return
    const eigenanteilMin = Math.max(0, massnahme.min - zuschuss)
    const eigenanteilMax = Math.max(0, massnahme.max - zuschuss)
    const eigenanteil = this.formatRange(eigenanteilMin, eigenanteilMax)
    this.kostenRangeTarget.textContent = this.formatRange(massnahme.min, massnahme.max)
    this.eigenanteilRangeTarget.textContent = eigenanteil

    if (this.hasVerdictTarget) this.verdictTarget.textContent = this.verdict(massnahme, eigenanteilMin, eigenanteilMax, hasPflegegrad)
    if (this.hasEigenanteilHintTarget) this.eigenanteilHintTarget.textContent = this.eigenanteilHint(eigenanteilMin, eigenanteilMax, hasPflegegrad)

    // Bleibt ein Eigenanteil, auf mögliche Landesförderung hinweisen
    // (Landes-Daten liegen für Badumbau und Treppenlift vor).
    if (this.hasLandesHintTarget) {
      this.landesHintTarget.classList.toggle("hidden", eigenanteilMax === 0)
    }
  }

  verdict(massnahme, min, max, hasPflegegrad) {
    const name = `„${massnahme.label}“`
    if (!hasPflegegrad) return `Ohne Pflegegrad zahlen Sie für ${name} die vollen ${this.formatRange(massnahme.min, massnahme.max)} selbst.`
    if (max === 0) return `Der Zuschuss deckt die typischen Kosten für ${name} komplett, es bleibt kein Eigenanteil.`
    return `Nach Zuschuss bleiben für ${name} je nach Angebot ${this.formatRange(min, max)} Eigenanteil.`
  }

  // Unterzeile der Eigenanteil-Kachel; spiegelt start_eigen_hint im Partial
  eigenanteilHint(min, max, hasPflegegrad) {
    if (!hasPflegegrad) return "voller Preis ohne Pflegegrad"
    if (max === 0) return "Der Zuschuss deckt die typischen Kosten komplett."
    if (min === 0) return `Bei günstigen Angeboten ${this.formatter.format(0)}`
    return "je nach Angebot innerhalb der Spanne"
  }

  // "0 € – 0 €" (or any equal bounds) collapses to a single value
  formatRange(min, max) {
    if (min === max) return this.formatter.format(min)
    return `${this.formatter.format(min)}\u00a0– ${this.formatter.format(max)}`
  }

  // Optionale PLZ-Zuordnung: PLZ → Bundesland via /api/zip/:plz,
  // dann Kurzinfo zum Landesförderprogramm aus data.landesfoerderung anzeigen.
  async lookupPlz() {
    const plz = this.plzInputTarget.value.trim()
    if (!/^\d{5}$/.test(plz)) {
      this.renderPlzResult(null)
      return
    }

    try {
      const response = await fetch(`/api/zip/${plz}`)
      if (!response.ok) return
      const data = await response.json()
      this.renderPlzResult(data.valid ? data : false)
    } catch (_) { /* network error — leave the box unchanged */ }
  }

  renderPlzResult(zip) {
    if (!this.hasPlzResultTarget) return
    const box = this.plzResultTarget

    if (zip === null) {
      box.classList.add("hidden")
      return
    }
    if (zip === false) {
      box.classList.remove("hidden")
      box.textContent = "Postleitzahl nicht gefunden — bitte prüfen Sie die Eingabe."
      return
    }
    // Edge-Cache-Antworten von vor dem bundesland-Feld: gültige PLZ, aber
    // keine Zuordnung möglich — dann lieber nichts anzeigen als eine Fehlermeldung.
    if (!zip.bundesland) {
      box.classList.add("hidden")
      return
    }

    const program = (this.dataValue.landesfoerderung || {})[zip.bundesland]
    const link = `<a href="#landesfoerderung" class="text-ph24_new_blue underline underline-offset-2 font-semibold">Landesförderung</a>`
    box.classList.remove("hidden")
    if (!program || program.art === "keins") {
      box.innerHTML = `<strong>${zip.bundesland}:</strong> kein eigenes Landesprogramm für Privathaushalte — ` +
        `es bleiben Pflegekasse, KfW und Steuerwege. Details: ${link}.`
    } else {
      box.innerHTML = `<strong>Zusätzlich in ${zip.bundesland}:</strong> ${program.bank} fördert mit ` +
        `${program.betrag} (${program.art_label}). Details und Bedingungen: ${link}.`
    }
  }
}
